import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { routePaths } from 'components/Routes/data';
import {
  Box,
  Text,
  Amount,
  Avatar,
  Link,
  Button,
  Heading,
  Spinner,
} from '@razorpay/blade/components';
import {
  FormFieldsWrapper,
  FormFooterWrapper,
  StyledForm,
} from 'components/EmployeeOnboarding/components/styles';
import useReduxSelector from 'utils/useReduxSelector';
import { ErrorInfoModal } from './components/ErrorInfoModal';
import { useSavedEmployeeData } from './hooks/useSavedEmployeeData';
import { getStateName } from './utils';
import { RearchSalaryStructurePreview } from '../CreateEmployee/components/salaryStructure/RearchSalaryStructurePreview';
import useIsFeatureDisabledForRearch from 'access/useIsFeatureDisabledForRearch';
import { DefaultSalaryStructurePreview } from '../CreateEmployee/components/salaryStructure/DefaultSalaryStructurePreview';
import { CustomSalaryStructurePreview } from '../CreateEmployee/components/salaryStructure/CustomSalaryStructurePreview';
import useFeature from 'hooks/useFeature';
import { useOfficeLocationById } from '../CreateEmployee/hooks/useOfficeLocationById';

export const PreviewInformation = () => {
  const history = useHistory();
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const organizationName = useReduxSelector(
    (state) => state.loggedInUser.data?.currentOrganization?.name,
  );

  const {
    employeeDetails: { basicDetails, compensationDetails },
    isLoadingQuery,
  } = useSavedEmployeeData();

  const { isEsicMultiLocationEnabled } = useFeature('esicMultiLocation');

  const { data: selectedOfficeLocation, isFetchingOfficeLocation } = useOfficeLocationById(
    basicDetails?.officeLocationId,
  );

  const { isMileStone1Enabled: isRearchEnabled } = useIsFeatureDisabledForRearch();

  const handleStartOnboarding = () => {
    history.push(routePaths.employeeOnboarding.employeeSelfOnboarding.kycDetails);
  };

  const location = isEsicMultiLocationEnabled
    ? selectedOfficeLocation?.office_name
    : getStateName(basicDetails?.location);

  if (isLoadingQuery || isFetchingOfficeLocation) {
    return (
      <Box>
        <Spinner accessibilityLabel="Loading" />
      </Box>
    );
  }

  return (
    <StyledForm>
      <FormFieldsWrapper>
        {/* Welcome Section */}
        <Box>
          <Heading size="large" weight="semibold" color="surface.text.gray.normal" as="span">
            Welcome to{' '}
          </Heading>
          <Heading size="large" weight="regular" color="surface.text.gray.muted" as="span">
            {organizationName}!
          </Heading>
          <Text
            size="medium"
            weight="regular"
            marginTop="spacing.2"
            color="surface.text.gray.muted"
            display="block">
            You've joined the right place, and we're excited to have you on board!
          </Text>
        </Box>

        {/* Employee Info Card */}
        <Box borderRadius="medium" borderWidth="thin" borderColor="surface.border.gray.muted">
          <Box
            backgroundColor="surface.background.gray.intense"
            display="flex"
            gap="spacing.5"
            alignItems="center"
            padding="spacing.5"
            borderRadius="medium"
            justifyContent="space-between">
            <Box display="flex" alignItems="center" gap="spacing.4">
              <Avatar color="information" variant="square" name={basicDetails?.name} size="large" />
              <Box>
                <Text weight="semibold" color="surface.text.gray.normal">
                  {basicDetails?.name}
                </Text>
                <Text size="small" color="surface.text.gray.muted">
                  {basicDetails?.designation} | {location}
                </Text>
              </Box>
            </Box>
            <Link color="primary" onClick={() => setIsErrorModalOpen(true)}>
              Something wrong?
            </Link>
          </Box>

          <Box
            display="flex"
            gap="spacing.11"
            padding="spacing.5"
            width="100%"
            justifyContent="space-between">
            <Box>
              <Text size="small" color="surface.text.gray.muted">
                Email Address
              </Text>
              <Text size="small" color="surface.text.gray.normal">
                {basicDetails?.email}
              </Text>
            </Box>
            <Box>
              <Text size="small" color="surface.text.gray.muted">
                Manager
              </Text>
              <Text size="small" color="surface.text.gray.normal">
                {basicDetails?.managerName ?? '-'}
              </Text>
            </Box>
            <Box>
              <Text size="small" color="surface.text.gray.muted">
                Joining Date
              </Text>
              <Text size="small" color="surface.text.gray.normal">
                {basicDetails?.hireDate}
              </Text>
            </Box>
          </Box>
        </Box>

        {/* Salary Structure Section */}
        <Box>
          <Box marginBottom="spacing.8">
            <Box display="flex" alignItems="center" gap="spacing.3">
              <Heading size="medium" weight="semibold">
                👇🏻 Here's your Salary Structure
              </Heading>
            </Box>
            <Text size="small" variant="caption" color="surface.text.gray.muted">
              Please complete the following steps to ensure your salary is cha-chinged on time every
              month.
            </Text>
          </Box>

          <Box marginBottom="spacing.8">
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              marginBottom="spacing.5">
              <Box>
                <Box display="flex" alignItems="baseline" gap="spacing.2">
                  <Heading size="medium" weight="semibold">
                    Earnings
                  </Heading>
                  <Heading size="medium" color="surface.text.gray.muted" weight="regular">
                    (CTC)
                  </Heading>
                </Box>
                <Text size="small" variant="caption" color="surface.text.gray.muted">
                  The total amount earned before the deductions
                </Text>
              </Box>
              <Amount
                value={compensationDetails?.ctc || 0}
                type="heading"
                size="large"
                weight="semibold"
              />
            </Box>

            {isRearchEnabled ? (
              <RearchSalaryStructurePreview annualCtc={compensationDetails?.ctc || 0} />
            ) : compensationDetails?.salaryStructureType === 'default' ? (
              <DefaultSalaryStructurePreview />
            ) : (
              <CustomSalaryStructurePreview compensationDetails={compensationDetails} />
            )}
          </Box>
        </Box>
      </FormFieldsWrapper>

      <FormFooterWrapper>
        <Button variant="tertiary" onClick={() => history.push('/')}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleStartOnboarding}>
          Start Onboarding
        </Button>
      </FormFooterWrapper>
      <ErrorInfoModal isOpen={isErrorModalOpen} onClose={() => setIsErrorModalOpen(false)} />
    </StyledForm>
  );
};
